/**
 * ExecPathwayFlow — AAF Contracting Cone parcat with brushable histograms.
 *
 * Sub-page of /exec/explore. Loads pre-aggregated path-grain rows from
 * /api/govdata/pathway-flow/, renders the notebook's parcat via Vega-Lite,
 * and shows three brushable histograms stacked vertically on the right.
 * Brushes fade ribbons by per-path pass-fraction; category clicks on any
 * axis filter ribbons (AND across axes).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, GitBranch } from 'lucide-react'
import { execAPI } from '../services/exec'
import type { PortfolioGroup } from '../types/exec'
import PathwayFlowChart, { type Selection } from '../components/exec/pathway-flow/PathwayFlowChart'
import BrushHistogram from '../components/exec/pathway-flow/BrushHistogram'
import {
  usePathwayFlowQuery, PATHWAY_AXES,
  type PathwayAxis, type PathwayRow,
  DAYS_OPEN_BIN_WIDTH, DAYS_EVAL_BIN_WIDTH, LOG_AMOUNT_BIN_WIDTH, N_BINS,
} from '../components/exec/pathway-flow/usePathwayFlowQuery'

// pathway_flow carries full data for FY2020–2025 (earlier years are sparse,
// FY2026 has barely started). Default to the latest complete fiscal year.
const DEFAULT_FY = 2025
const FY_OPTIONS = [2020, 2021, 2022, 2023, 2024, 2025]

/**
 * The award-amount histogram is binned in log10 space (the mart stores
 * `log_amount` bins). Leaders don't read logs, so label the axis with the
 * actual dollar amount each bin starts at — 10 ** log10Value — formatted
 * compactly ($1, $4K, $1M, $1B…).
 */
function formatAwardAmount(log10Value: number): string {
  const dollars = Math.pow(10, log10Value)
  if (dollars < 1e3) return `$${Math.round(dollars)}`
  if (dollars < 1e6) return `$${Math.round(dollars / 1e3)}K`
  if (dollars < 1e9) return `$${Math.round(dollars / 1e6)}M`
  if (dollars < 1e12) return `$${Math.round(dollars / 1e9)}B`
  return `$${Math.round(dollars / 1e12)}T`
}

export default function ExecPathwayFlow() {
  const [portfolioGroups, setPortfolioGroups] = useState<PortfolioGroup[]>([])
  const [portfolioGroupId, setPortfolioGroupId] = useState('')
  const [fiscalYear, setFiscalYear] = useState<number>(DEFAULT_FY)

  const [selection, setSelection] = useState<Selection>({})
  const [openBrush, setOpenBrush] = useState<[number, number] | null>(null)
  const [evalBrush, setEvalBrush] = useState<[number, number] | null>(null)
  const [amountBrush, setAmountBrush] = useState<[number, number] | null>(null)

  // Measure the chart pane so the parcat fits horizontally instead of
  // overflowing — its width tracks the available space (min 480px).
  const chartPaneRef = useRef<HTMLElement | null>(null)
  const [chartWidth, setChartWidth] = useState(900)

  useEffect(() => {
    execAPI.getPortfolioGroups().then(setPortfolioGroups).catch(() => {})
  }, [])

  useEffect(() => {
    const el = chartPaneRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (w) setChartWidth(Math.max(Math.floor(w), 480))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const { data, loading, error } = usePathwayFlowQuery({
    fiscalYear,
    portfolioGroup: portfolioGroupId || undefined,
  })

  const aggregateBins = useMemo(() => computeAggregateBins(data, selection), [data, selection])
  const brushes = useMemo(() => ({ open: openBrush, eval: evalBrush, amount: amountBrush }),
    [openBrush, evalBrush, amountBrush])
  const summary = useMemo(() => summarize(data, selection, brushes), [data, selection, brushes])

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="border-b border-border bg-card px-6 py-3 flex items-center gap-3">
        <Link
          to="/exec/explore"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} /> Explore
        </Link>
        <span className="text-muted-foreground">/</span>
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <GitBranch size={18} /> Pathway Flow
        </h1>
        <span className="text-xs text-muted-foreground ml-auto">
          {summary.totalPaths.toLocaleString()} paths · {summary.totalAwards.toLocaleString()} awards
          {summary.selectedAwards !== summary.totalAwards && ` · ${summary.selectedAwards.toLocaleString()} selected`}
        </span>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 border-r border-border bg-card p-4 overflow-y-auto space-y-4">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Fiscal Year</label>
            <select
              className="w-full text-sm rounded border border-border bg-background px-2 py-1"
              value={fiscalYear}
              onChange={(e) => setFiscalYear(Number(e.target.value))}
            >
              {FY_OPTIONS.map((y) => (
                <option key={y} value={y}>FY{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Portfolio</label>
            <select
              className="w-full text-sm rounded border border-border bg-background px-2 py-1"
              value={portfolioGroupId}
              onChange={(e) => setPortfolioGroupId(e.target.value)}
            >
              <option value="">All DoW</option>
              {portfolioGroups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>

          <ActiveSelections selection={selection} onClear={(axis) => {
            setSelection((prev) => { const n = { ...prev }; delete n[axis]; return n })
          }} onClearAll={() => setSelection({})} />

          <div className="text-xs text-muted-foreground pt-3 border-t border-border">
            Click category bars on any axis to filter. Brush a histogram to
            fade ribbons whose paths fall outside the range.
          </div>
        </aside>

        {/* Main: parcat on the left, histograms stacked on the right */}
        <main className="flex-1 flex overflow-hidden">
          <section ref={chartPaneRef} className="flex-1 min-w-0 p-3 overflow-auto">
            {loading && <div className="text-sm text-muted-foreground">Loading paths…</div>}
            {error && <div className="text-sm text-destructive">{error}</div>}
            {!loading && !error && (
              <PathwayFlowChart
                rows={data}
                brushes={brushes}
                selection={selection}
                onSelectionChange={setSelection}
                width={chartWidth}
                height={620}
              />
            )}
          </section>
          <section className="w-[320px] shrink-0 border-l border-border bg-card p-3 flex flex-col gap-2 overflow-auto">
            <BrushHistogram
              title="Days open for response"
              bins={aggregateBins.daysOpen}
              binWidth={DAYS_OPEN_BIN_WIDTH}
              unitLabel="days"
              brush={openBrush}
              onBrushChange={setOpenBrush}
              color="oklch(0.55 0.16 240)"
              height={180}
            />
            <BrushHistogram
              title="Days response → award"
              bins={aggregateBins.daysEval}
              binWidth={DAYS_EVAL_BIN_WIDTH}
              unitLabel="days"
              brush={evalBrush}
              onBrushChange={setEvalBrush}
              color="oklch(0.62 0.16 150)"
              height={180}
            />
            <BrushHistogram
              title="Award amount"
              bins={aggregateBins.logAmount}
              binWidth={LOG_AMOUNT_BIN_WIDTH}
              unitLabel=""
              binLabel={(i) => formatAwardAmount(i * LOG_AMOUNT_BIN_WIDTH)}
              brush={amountBrush}
              onBrushChange={setAmountBrush}
              color="oklch(0.65 0.16 40)"
              height={180}
            />
          </section>
        </main>
      </div>
    </div>
  )
}

function ActiveSelections({
  selection, onClear, onClearAll,
}: {
  selection: Selection
  onClear: (axis: PathwayAxis) => void
  onClearAll: () => void
}) {
  const entries = Object.entries(selection) as [PathwayAxis, Set<string>][]
  if (entries.length === 0) return null
  return (
    <div className="border-t border-border pt-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold">Selections</span>
        <button
          className="text-xs text-muted-foreground hover:text-foreground"
          onClick={onClearAll}
        >
          Clear all
        </button>
      </div>
      <div className="space-y-1">
        {entries.map(([axis, set]) => (
          <div key={axis} className="text-xs">
            <button
              className="font-medium hover:underline"
              onClick={() => onClear(axis)}
              title="Clear this axis"
            >
              {axis} ({set.size}) ×
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

function computeAggregateBins(
  rows: PathwayRow[], selection: Selection,
): { daysOpen: number[]; daysEval: number[]; logAmount: number[] } {
  const daysOpen = new Array(N_BINS).fill(0)
  const daysEval = new Array(N_BINS).fill(0)
  const logAmount = new Array(N_BINS).fill(0)
  for (const r of rows) {
    if (!matchesSelection(r, selection)) continue
    for (let i = 0; i < N_BINS; i++) {
      daysOpen[i] += r.days_open_bins[i] ?? 0
      daysEval[i] += r.days_eval_bins[i] ?? 0
      logAmount[i] += r.log_amount_bins[i] ?? 0
    }
  }
  return { daysOpen, daysEval, logAmount }
}

function matchesSelection(row: PathwayRow, sel: Selection): boolean {
  for (const axis of PATHWAY_AXES) {
    const allowed = sel[axis]
    if (allowed && allowed.size > 0 && !allowed.has(String(row[axis]))) return false
  }
  return true
}

function summarize(
  rows: PathwayRow[], selection: Selection,
  brushes: { open: [number, number] | null; eval: [number, number] | null; amount: [number, number] | null },
): { totalPaths: number; totalAwards: number; selectedAwards: number } {
  let totalAwards = 0
  let selectedAwards = 0
  const brushSpec = [
    ['days_open_bins', brushes.open] as const,
    ['days_eval_bins', brushes.eval] as const,
    ['log_amount_bins', brushes.amount] as const,
  ]
  for (const r of rows) {
    totalAwards += r.award_count
    if (!matchesSelection(r, selection)) continue
    let frac = 1
    for (const [key, brush] of brushSpec) {
      if (!brush) continue
      const bins = r[key] as number[]
      const total = bins.reduce((s, n) => s + n, 0) || 1
      let inside = 0
      for (let i = brush[0]; i <= brush[1] && i < N_BINS; i++) inside += bins[i] ?? 0
      frac *= inside / total
    }
    selectedAwards += Math.round(r.award_count * frac)
  }
  return { totalPaths: rows.length, totalAwards, selectedAwards }
}
